import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Check, Zap, TrendingUp, Clock } from "lucide-react";
import { useSubscriptionStore } from "@/store/subscriptionStore";
import { useAuthStore } from "@/store/authStore";
import { useEffect } from "react";

export function UpgradeDialog() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const {
    showUpgradeDialog,
    setShowUpgradeDialog,
    currentTrigger,
    createCheckout,
    createPaystackSubscription,
  } = useSubscriptionStore();

  // Only show for admin/manager
  if (!user || !["admin", "manager"].includes(user.role)) {
    return null;
  }

  // Determine recommended plan based on trigger
  const getRecommendedPlan = () => {
    if (!currentTrigger) {
      return null;
    }

    switch (currentTrigger.type) {
      case "store_limit":
        return {
          name: "Professional",
          price: "15",
          yearlyPrice: "13.20",
          paystackPlanCode: "PLN_ty418792cqfcukr",
          features: t("pricingSection.plans.professional.features", {
            returnObjects: true,
          }) as string[],
        };
      case "feature_access":
        return {
          name: "Enterprise",
          price: "27",
          yearlyPrice: "23.76",
          paystackPlanCode: "PLN_mj9xzfh93j6xmw4",
          features: t("pricingSection.plans.enterprise.features", {
            returnObjects: true,
          }) as string[],
        };
      default:
        return {
          name: "Professional",
          price: "15",
          yearlyPrice: "13.20",
          paystackPlanCode: "PLN_ty418792cqfcukr",
          features: t("pricingSection.plans.professional.features", {
            returnObjects: true,
          }) as string[],
        };
    }
  };

  const recommendedPlan = getRecommendedPlan();
  if (!recommendedPlan) {
    return null;
  }

  // Get ROI metrics based on trigger
  const getRoiMetrics = () => {
    if (!currentTrigger) return [];

    switch (currentTrigger.type) {
      case "store_limit":
        return [
          "300% average revenue increase with multi-store management",
          "Streamlined operations across all locations",
          "Centralized inventory and staff management",
        ];
      case "menu_limit":
        return [
          "40% more orders with expanded menu options",
          "Better customer satisfaction with variety",
          "Seasonal menu flexibility",
        ];
      case "feature_access":
        return [
          "Save 5+ hours/week on staff management",
          "50% increase in table turnover with reservations",
          "Improved customer engagement with premium features",
        ];
    }
  };

  const handleStripeUpgrade = async (billingPeriod: "monthly" | "yearly") => {
    try {
      await createCheckout(recommendedPlan.name.toLowerCase(), billingPeriod);
      setShowUpgradeDialog(false);
    } catch (error) {
      console.error("Error creating checkout:", error);
    }
  };

  const handlePaystackUpgrade = async () => {
    try {
      if (!user?.email || !recommendedPlan.paystackPlanCode) return;

      await createPaystackSubscription(
        recommendedPlan.paystackPlanCode,
        user.email
      );
      setShowUpgradeDialog(false);
    } catch (error) {
      console.error("Error creating Paystack subscription:", error);
    }
  };

  return (
    <Dialog
      open={showUpgradeDialog}
      onOpenChange={(open) => {
        console.log("Dialog onOpenChange:", open);
        setShowUpgradeDialog(open);
      }}
    >
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Zap className="h-6 w-6 text-primary" />
            {currentTrigger?.type === "store_limit"
              ? "Expand Your Business"
              : currentTrigger?.type === "menu_limit"
              ? "Unlock Full Product Catalog"
              : "Access Premium Features"}
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            {currentTrigger?.currentUsage && (
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary" className="text-sm">
                  Current Usage: {currentTrigger.currentUsage.current}/
                  {currentTrigger.currentUsage.limit}
                </Badge>
              </div>
            )}
            Upgrade to unlock your business's full potential
          </DialogDescription>
        </DialogHeader>

        {/* Value Proposition */}
        <div className="space-y-4 py-4">
          {/* ROI Metrics */}
          <Card className="border-primary/20">
            <CardContent className="pt-6">
              <h3 className="font-semibold flex items-center gap-2 mb-3">
                <TrendingUp className="h-5 w-5 text-primary" />
                Growth Impact
              </h3>
              <ul className="space-y-2">
                {getRoiMetrics().map((metric, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{metric}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Social Proof */}
          <div className="bg-muted/50 rounded-lg p-4">
            <blockquote className="text-sm italic">
              "After upgrading to {recommendedPlan.name}, we saw a 200% increase
              in orders within the first month!"
            </blockquote>
            <p className="text-sm text-muted-foreground mt-2">
              - Similar business in your area
            </p>
          </div>

          {/* Limited Time Offer */}
          <div className="flex items-center gap-2 text-primary">
            <Clock className="h-5 w-5" />
            <span className="text-sm font-medium">
              Limited time offer: Save 12% with annual billing
            </span>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-col gap-2">
          <div className="flex flex-col gap-2 w-full">
            <Button
              size="lg"
              className="w-full"
              onClick={() => handleStripeUpgrade("yearly")}
            >
              Upgrade with Stripe Yearly
              <span className="text-sm ml-2">
                (€{recommendedPlan.yearlyPrice.replace("€", "")}/month)
              </span>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="w-full"
              onClick={() => handleStripeUpgrade("monthly")}
            >
              Upgrade with Stripe Monthly
              <span className="text-sm ml-2">
                (€{recommendedPlan.price.replace("€", "")}/month)
              </span>
            </Button>
            <Button
              size="lg"
              variant="secondary"
              className="w-full"
              onClick={handlePaystackUpgrade}
            >
              Upgrade with Paystack
              <span className="text-sm ml-2">
                (NGN{" "}
                {recommendedPlan.name === "Professional" ? "19,900" : "28,000"}
                /month)
              </span>
            </Button>
          </div>
          <p className="text-xs text-center text-muted-foreground pt-2">
            30-day money-back guarantee • Cancel anytime • No setup fees
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
